/**
 * PÁGINA DE LOTES - VER LOTES DEL REMATE
 * ========================================
 * Página principal para ver y filtrar lotes del remate
 * Con filtros laterales y acordeón de categorías
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronDown, Search, X, Filter, Loader2
} from 'lucide-react';
import ModernHeader from '../components/modern/ModernHeader';
import ModernFooter from '../components/modern/ModernFooter';
import MapModal from '../components/MapModal';
import { api } from '../services/api';
import { useLoteEnCurso } from '../hooks/useLoteEnCurso';

// Subcomponents
import {
  LotesPageHeader,
  LoteEnCursoSection,
  LotesCategoriaAccordion
} from './LotesPage/index';

const LotesPage = () => {
  const navigate = useNavigate();

  // Estados para los datos
  const [allLotes, setAllLotes] = useState([]); // Todos los lotes del remate (sin filtrar)
  const [lotes, setLotes] = useState([]); // Lotes filtrados para mostrar

  const [filtrosDisponibles, setFiltrosDisponibles] = useState({});
  const [loading, setLoading] = useState(true);
  const [filtering, setFiltering] = useState(false); // Loading state for filter changes
  const [scrollY, setScrollY] = useState(0);

  // Estados para el remate seleccionado
  const [searchParams] = useSearchParams();
  const remateId = searchParams.get('remate');

  const [infoRemate, setInfoRemate] = useState({
    numero: '',
    fecha: '',
    totalLotes: 0,
    totalLotes: 0,
    estado: 'activo',
    fecha_formateada: '',
    en_curso: false,
    sello_url: null,
    catalogo_url: null
  });

  // Estados para filtros seleccionados
  const [filtrosActivos, setFiltrosActivos] = useState({
    escritorio: [],
    departamento: [],
    categoria: [],
    clase: [],
    estado: [],
    sellos: [],
    financiacion: [],
    preoferta: [],
    venta: [],
    pesoMin: '',
    pesoMax: ''
  });

  // Estados para acordeones
  const [filtrosAbiertos, setFiltrosAbiertos] = useState({
    categoria: false,
    escritorio: false,
    departamento: false,
    clase: false,
    estado: false,
    sellos: false,
    financiacion: false,
    preoferta: false,
    venta: false
  });

  const [categoriasAbiertas, setCategoriasAbiertas] = useState({});
  const [categoriasExpandiendo, setCategoriasExpandiendo] = useState({}); // Estado para mostrar loading al expandir
  const [lotesAbiertos, setLotesAbiertos] = useState({});
  const [menuFiltrosMobile, setMenuFiltrosMobile] = useState(false);
  const [tipoGanado, setTipoGanado] = useState('Vacunos'); // Estado para tipo de ganado seleccionado
  const [tiposDisponibles, setTiposDisponibles] = useState(['Vacunos']); // 'Vacunos', 'Ovinos', 'Toros'
  const [showMapModal, setShowMapModal] = useState(false);

  // Sincronización con Firestore para lote en curso
  const { loteEnCurso, pique, isLoading: loadingLoteEnCurso } = useLoteEnCurso(
    infoRemate.en_curso ? infoRemate.numero : null
  );
  const [loteEnCursoData, setLoteEnCursoData] = useState(null);
  const [loadingLoteEnCursoData, setLoadingLoteEnCursoData] = useState(false);

  // Manejar scroll para header
  // useEffect(() => {
  //   const handleScroll = () => {
  //     setScrollY(window.scrollY);
  //   };
  //   window.addEventListener('scroll', handleScroll);
  //   return () => window.removeEventListener('scroll', handleScroll);
  // }, []);

  // Fallback: redirect to first active remate if no remate param
  useEffect(() => {
    const fetchFirstRemate = async () => {
      if (remateId) return; // Already have a remate param

      try {
        const remates = await api.getRematesList();
        // Find first active remate (estado = 2 is active, estado = 3 is in progress)
        const activeRemate = remates.find(r => r.estado === 2 || r.estado === 3) || remates[0];
        if (activeRemate) {
          navigate(`/lotes?remate=${activeRemate.numero}`, { replace: true });
        }
      } catch (error) {
        console.error('Error fetching first remate:', error);
      }
    };
    fetchFirstRemate();
  }, [remateId, navigate]);

  // Cargar información del remate seleccionado
  useEffect(() => {
    const fetchInfoRemate = async () => {
      if (!remateId) return;

      try {
        const info = await api.getRemateInfo(remateId);

        setInfoRemate({
          numero: info.numero,
          fecha: info.fecha_formateada,
          totalLotes: info.total_lotes,
          estado: info.estado,
          fecha_formateada: info.fecha_formateada,
          en_curso: info.en_curso,
          sello_url: info.sello_url,
          catalogo_url: info.catalogo_url,
          lugar: info.lugar
        });
      } catch (error) {
        console.error('Error cargando info del remate:', error);
      }
    };
    fetchInfoRemate();
  }, [remateId]);

  // Cargar datos del lote en curso cuando cambie
  useEffect(() => {
    const fetchLoteEnCursoData = async () => {
      if (!loteEnCurso || !infoRemate.en_curso) {
        setLoteEnCursoData(null);
        return;
      }

      // Buscar en los lotes ya cargados
      const loteFromList = allLotes.find(l => l.numero === loteEnCurso);
      if (loteFromList) {
        // Si lo encontramos, cargamos los datos completos del lote
        setLoadingLoteEnCursoData(true);
        try {
          const loteCompleto = await api.getLoteDetalle(loteFromList.id);
          setLoteEnCursoData(loteCompleto);
        } catch (error) {
          console.error('Error cargando lote en curso:', error);
          setLoteEnCursoData(null);
        }
        setLoadingLoteEnCursoData(false);
      } else if (allLotes.length > 0) {
        // Si allLotes ya está cargado pero no encontramos el lote, no hacer nada
        // (evitamos llamar de nuevo a getLotesRemate)
        console.log('Lote en curso no encontrado en la lista cargada');
        setLoteEnCursoData(null);
      }
      // Si allLotes aún no está cargado (length === 0), esperamos a que se cargue
    };

    fetchLoteEnCursoData();
  }, [loteEnCurso, infoRemate.en_curso, allLotes]);

  // Cargar opciones de filtros dinámicamente desde los lotes
  useEffect(() => {
    if (allLotes.length > 0) {
      const extractUnique = (key) => [...new Set(allLotes.map(l => l[key]).filter(Boolean))].sort();

      setFiltrosDisponibles({
        categorias: extractUnique('categoria'),
        escritorios: extractUnique('escritorio'),
        departamentos: extractUnique('departamento'),
        clases: extractUnique('clase'), // Placeholder por ahora
        estados: extractUnique('estado'), // Placeholder por ahora
        sellos: [], // No disponible en datos actuales
        financiacion: [], // No disponible
        preoferta: [], // No disponible
        venta: [] // No disponible
      });
    }
  }, [allLotes]);

  // Ref para evitar llamadas duplicadas al endpoint (problema con StrictMode en dev)
  const fetchingLotesRef = React.useRef(false);
  const lastRemateIdRef = React.useRef(null);

  // Cargar lotes del remate seleccionado
  useEffect(() => {
    const fetchLotes = async () => {
      if (!remateId) return;

      // Evitar llamadas duplicadas
      if (fetchingLotesRef.current && lastRemateIdRef.current === remateId) {
        return;
      }

      fetchingLotesRef.current = true;
      lastRemateIdRef.current = remateId;
      setLoading(true);

      try {
        // Usar el nuevo endpoint que trae todos los lotes del remate con joins complejos
        const data = await api.getLotesRemate(remateId);

        // Mapear datos del backend al formato del frontend
        const mappedLotes = data.map(lote => ({
          ...lote,
          numero_lote: lote.numero,
          inspeccion_numero: lote.inspeccion_numero,
          cabezas: lote.cantidad,
          raza: lote.razas || lote.raza || '', // Usar raza obtenida del backend
          vendedor: lote.escritorio, // Escritorio como vendedor
          latitude: lote.latitud,
          longitude: lote.longitud,
          estado: lote.estado || '', // Usar estado del backend
          clase: lote.clase || '', // Usar clase del backend
          peso_min: 0,
          peso_max: 0,
          peso_promedio: lote.peso_promedio || 0,
          precio_min: 0,
          precio_max: 0,
          precio_promedio: lote.promedio_categoria || 0,
          horario: lote.horario || '',
          vendedor: lote.vendedor || '',
          venta_estado: lote.venta_estado || 'PE',
          preoferta: lote.preoferta || 0,
          vendido: lote.vendido || false,
          precio_venta: lote.precio_venta || 0,
          escritorio_logo: lote.escritorio_logo || ''
        }));

        setAllLotes(mappedLotes);

        // Determinar tipos disponibles
        const tipos = [];
        if (mappedLotes.some(l => l.tipo_ganado === 'V')) tipos.push('Vacunos');
        if (mappedLotes.some(l => l.tipo_ganado === 'L')) tipos.push('Ovinos');
        if (mappedLotes.some(l => l.tipo_ganado === 'T')) tipos.push('Toros');

        setTiposDisponibles(tipos);

        // Si el tipo seleccionado no está disponible, seleccionar el primero
        if (!tipos.includes(tipoGanado) && tipos.length > 0) {
          setTipoGanado(tipos[0]);
        }

        // Don't setLotes here - let the filter useEffect handle it
        // Don't setLoading(false) here - the filter useEffect will do it
      } catch (error) {
        console.error('Error cargando lotes:', error);
        setAllLotes([]);
        setLotes([]);
        setLoading(false); // Only set loading false on error
      } finally {
        fetchingLotesRef.current = false;
      }
    };

    fetchLotes();
  }, [remateId]);

  // Aplicar filtros en el frontend
  useEffect(() => {
    // Skip if no data loaded yet
    if (allLotes.length === 0) {
      return;
    }

    // Show filtering indicator
    setFiltering(true);

    // Use setTimeout to allow the UI to update with the loading state
    const filterTimeout = setTimeout(() => {
      let result = [...allLotes];

      // Filtro: Tipo de Ganado (Vacunos/Ovinos/Toros)
      // El backend devuelve tipo_ganado: 'V' (Vacuno), 'L' (Ovino/Lanar), 'T' (Toro)
      if (tipoGanado === 'Vacunos') {
        result = result.filter(l => l.tipo_ganado === 'V');
      } else if (tipoGanado === 'Ovinos') {
        result = result.filter(l => l.tipo_ganado === 'L');
      } else if (tipoGanado === 'Toros') {
        result = result.filter(l => l.tipo_ganado === 'T');
      }

      // Filtro: Escritorio
      if (filtrosActivos.escritorio.length > 0) {
        result = result.filter(l => filtrosActivos.escritorio.includes(l.escritorio));
      }

      // Filtro: Departamento
      if (filtrosActivos.departamento.length > 0) {
        result = result.filter(l => filtrosActivos.departamento.includes(l.departamento));
      }

      // Filtro: Categoría
      if (filtrosActivos.categoria.length > 0) {
        result = result.filter(l => filtrosActivos.categoria.includes(l.categoria));
      }

      // Filtro: Preoferta
      if (filtrosActivos.preoferta.length > 0) {
        if (filtrosActivos.preoferta.includes('Con Preoferta')) {
          result = result.filter(l => l.preoferta > 0);
        } else if (filtrosActivos.preoferta.includes('Sin Preoferta')) {
          result = result.filter(l => l.preoferta === 0);
        }
      }

      // Filtro: Venta
      if (filtrosActivos.venta.length > 0) {
        if (filtrosActivos.venta.includes('Vendido')) {
          result = result.filter(l => l.vendido === true);
        } else if (filtrosActivos.venta.includes('No Vendido')) {
          result = result.filter(l => l.vendido === false);
        }
      }

      // Filtro: Peso
      if (filtrosActivos.pesoMin) {
        result = result.filter(l => l.peso_promedio >= parseFloat(filtrosActivos.pesoMin));
      }
      if (filtrosActivos.pesoMax) {
        result = result.filter(l => l.peso_promedio <= parseFloat(filtrosActivos.pesoMax));
      }

      setLotes(result);
      setFiltering(false);
      setLoading(false); // Turn off initial loading after first filter
    }, 50); // Small delay to show the loading state

    return () => clearTimeout(filterTimeout);
  }, [allLotes, filtrosActivos, tipoGanado]);

  // Agrupar lotes por categoría
  const lotesPorCategoria = lotes.reduce((acc, lote) => {
    if (!acc[lote.categoria]) {
      acc[lote.categoria] = [];
    }
    acc[lote.categoria].push(lote);
    return acc;
  }, {});

  // Toggle filtro
  const toggleFiltro = (tipoFiltro, valor) => {
    setFiltrosActivos(prev => {
      const filtrosActuales = prev[tipoFiltro];
      const existe = filtrosActuales.includes(valor);

      return {
        ...prev,
        [tipoFiltro]: existe
          ? filtrosActuales.filter(v => v !== valor)
          : [...filtrosActuales, valor]
      };
    });
  };

  // Limpiar todos los filtros
  const limpiarFiltros = () => {
    setFiltrosActivos({
      escritorio: [],
      departamento: [],
      categoria: [],
      clase: [],
      estado: [],
      sellos: [],
      financiacion: [],
      preoferta: [],
      venta: [],
      pesoMin: '',
      pesoMax: ''
    });
  };

  // Toggle acordeón de filtro
  const toggleAcordeonFiltro = (filtro) => {
    setFiltrosAbiertos(prev => ({
      ...prev,
      [filtro]: !prev[filtro]
    }));
  };

  // Toggle categoría con indicador de carga para muchos lotes
  const toggleCategoria = (categoria) => {
    const lotesEnCategoria = lotesPorCategoria[categoria]?.length || 0;
    const estaAbierta = categoriasAbiertas[categoria];

    if (!estaAbierta && lotesEnCategoria > 10) {
      // Si hay muchos lotes, mostrar indicador de carga
      setCategoriasExpandiendo(prev => ({ ...prev, [categoria]: true }));
      setCategoriasAbiertas(prev => ({ ...prev, [categoria]: true }));

      // Pequeño delay para permitir que React renderice el spinner primero
      requestAnimationFrame(() => {
        setTimeout(() => {
          setCategoriasExpandiendo(prev => ({ ...prev, [categoria]: false }));
        }, 100);
      });
    } else {
      // Para pocas lotes o cerrar, comportamiento normal
      setCategoriasAbiertas(prev => ({
        ...prev,
        [categoria]: !prev[categoria]
      }));
    }
  };

  // Toggle lote individual
  const toggleLote = (numeroLote) => {
    setLotesAbiertos(prev => ({
      ...prev,
      [numeroLote]: !prev[numeroLote]
    }));
  };

  // Contar filtros activos
  const totalFiltrosActivos = Object.values(filtrosActivos).reduce(
    (sum, arr) => sum + arr.length, 0
  );

  const getPermalink = (lote) => {
    // Estructura: https://plazarural.com.uy/remate/${remate_numero}/lote/${lote_numero}
    // Asumimos que infoRemate.numero está disponible
    return `https://plazarural.com.uy/remate/${infoRemate.numero}/lote/${lote.numero_lote}`;
  };

  const getShareText = (lote) => {
    const permalink = getPermalink(lote);
    return `Quiero compartir contigo el lote Nro ${lote.numero_lote} de ${lote.categoria} de ${lote.escritorio}: ${permalink}`;
  };

  const handleShare = (platform, lote) => {
    const text = getShareText(lote);
    const permalink = getPermalink(lote);
    const encodedText = encodeURIComponent(text);
    const encodedUrl = encodeURIComponent(permalink);

    let url = '';

    switch (platform) {
      case 'whatsapp':
        url = `https://api.whatsapp.com/send?text=${encodedText}`;
        break;
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${encodedText}`;
        break;
      case 'instagram':
        // Instagram no tiene API web directa para compartir texto/link facilmente
        // Copiamos al portapapeles
        navigator.clipboard.writeText(text).then(() => {
          alert('Texto copiado al portapapeles para compartir en Instagram');
        });
        return;
      default:
        return;
    }

    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Forzar header con fondo desde el inicio */}
      <ModernHeader scrollY={100} />

      {/* Contenido principal */}
      <div className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4">

          {/* Header de la página */}
          <LotesPageHeader
            infoRemate={infoRemate}
            onShowMapModal={() => setShowMapModal(true)}
          />

          {/* LOTE EN VIVO - Se muestra cuando el remate está en curso */}
          {infoRemate.en_curso && (loadingLoteEnCursoData || loteEnCursoData) && (
            <LoteEnCursoSection
              loteEnCurso={loteEnCurso}
              loteEnCursoData={loteEnCursoData}
              loadingLoteEnCursoData={loadingLoteEnCursoData}
              pique={pique}
            />
          )}

          <div className="grid lg:grid-cols-4 gap-6">

            {/* FILTROS LATERALES */}
            <div className="lg:col-span-1">
              {/* Botón mobile para abrir filtros */}
              <button
                onClick={() => setMenuFiltrosMobile(!menuFiltrosMobile)}
                className="lg:hidden w-full mb-4 flex items-center justify-center gap-2 bg-green-600 text-white py-3 rounded-lg"
              >
                <Filter className="w-5 h-5" />
                Filtros {totalFiltrosActivos > 0 && `(${totalFiltrosActivos})`}
              </button>

              {/* Panel de filtros */}
              <div className={`lg:block ${menuFiltrosMobile ? 'block' : 'hidden'} bg-white rounded-2xl shadow-2xl p-6 sticky top-24 border border-gray-100`}>
                <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-green-100">
                  <h3 className="text-xl font-bold bg-gradient-to-r from-green-600 to-green-500 bg-clip-text text-transparent flex items-center gap-2">
                    <Filter className="w-5 h-5 text-green-600" />
                    Filtros
                    {totalFiltrosActivos > 0 && (
                      <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full">
                        {totalFiltrosActivos}
                      </span>
                    )}
                  </h3>
                  {totalFiltrosActivos > 0 && (
                    <button
                      onClick={limpiarFiltros}
                      className="text-sm text-green-600 hover:text-green-700 font-bold bg-green-50 px-3 py-1 rounded-full transition-colors"
                    >
                      Limpiar
                    </button>
                  )}
                </div>

                {/* Filtros Acordeón */}
                <div className="space-y-3">

                  {/* Filtro: Categoría */}
                  {filtrosDisponibles.categorias && (
                    <div className="border-b border-gray-200 pb-3">
                      <button
                        onClick={() => toggleAcordeonFiltro('categoria')}
                        className="w-full flex items-center justify-between py-2 hover:bg-gray-50 px-2 rounded transition-colors duration-200"
                      >
                        <span className="font-semibold text-gray-900">
                          Categoría ({filtrosActivos.categoria.length > 0 ? filtrosActivos.categoria.length : 0})
                        </span>
                        <ChevronDown
                          className={`w-5 h-5 text-gray-500 transition-transform duration-300 ease-in-out ${filtrosAbiertos.categoria ? 'rotate-180' : 'rotate-0'
                            }`}
                        />
                      </button>

                      <div
                        style={{
                          maxHeight: filtrosAbiertos.categoria ? '200px' : '0',
                          opacity: filtrosAbiertos.categoria ? 1 : 0,
                          marginTop: filtrosAbiertos.categoria ? '0.5rem' : '0',
                          transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease-in-out, margin-top 0.3s ease-in-out'
                        }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {filtrosDisponibles.categorias.map(categoria => (
                            <label key={categoria} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={filtrosActivos.categoria.includes(categoria)}
                                onChange={() => toggleFiltro('categoria', categoria)}
                                className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                              />
                              <span className="text-sm text-gray-700">{categoria}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Filtro: Escritorio */}
                  {filtrosDisponibles.escritorios && (
                    <div className="border-b border-gray-200 pb-3">
                      <button
                        onClick={() => toggleAcordeonFiltro('escritorio')}
                        className="w-full flex items-center justify-between py-2 hover:bg-gray-50 px-2 rounded transition-colors duration-200"
                      >
                        <span className="font-semibold text-gray-900">
                          Escritorio ({filtrosActivos.escritorio.length > 0 ? filtrosActivos.escritorio.length : 0})
                        </span>
                        <ChevronDown
                          className={`w-5 h-5 text-gray-500 transition-transform duration-300 ease-in-out ${filtrosAbiertos.escritorio ? 'rotate-180' : 'rotate-0'
                            }`}
                        />
                      </button>

                      <div
                        style={{
                          maxHeight: filtrosAbiertos.escritorio ? '200px' : '0',
                          opacity: filtrosAbiertos.escritorio ? 1 : 0,
                          marginTop: filtrosAbiertos.escritorio ? '0.5rem' : '0',
                          transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease-in-out, margin-top 0.3s ease-in-out'
                        }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {filtrosDisponibles.escritorios.map(escritorio => (
                            <label key={escritorio} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={filtrosActivos.escritorio.includes(escritorio)}
                                onChange={() => toggleFiltro('escritorio', escritorio)}
                                className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                              />
                              <span className="text-sm text-gray-700">{escritorio}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Filtro: Departamento */}
                  {filtrosDisponibles.departamentos && (
                    <div className="border-b border-gray-200 pb-3">
                      <button
                        onClick={() => toggleAcordeonFiltro('departamento')}
                        className="w-full flex items-center justify-between py-2 hover:bg-gray-50 px-2 rounded transition-colors duration-200"
                      >
                        <span className="font-semibold text-gray-900">
                          Departamento ({filtrosActivos.departamento.length > 0 ? filtrosActivos.departamento.length : 0})
                        </span>
                        <ChevronDown
                          className={`w-5 h-5 text-gray-500 transition-transform duration-300 ease-in-out ${filtrosAbiertos.departamento ? 'rotate-180' : 'rotate-0'
                            }`}
                        />
                      </button>

                      <div
                        style={{
                          maxHeight: filtrosAbiertos.departamento ? '200px' : '0',
                          opacity: filtrosAbiertos.departamento ? 1 : 0,
                          marginTop: filtrosAbiertos.departamento ? '0.5rem' : '0',
                          transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease-in-out, margin-top 0.3s ease-in-out'
                        }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {filtrosDisponibles.departamentos.map(dept => (
                            <label key={dept} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={filtrosActivos.departamento.includes(dept)}
                                onChange={() => toggleFiltro('departamento', dept)}
                                className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                              />
                              <span className="text-sm text-gray-700">{dept}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Filtro: Clase */}
                  {filtrosDisponibles.clases && (
                    <div className="border-b border-gray-200 pb-3">
                      <button
                        onClick={() => toggleAcordeonFiltro('clase')}
                        className="w-full flex items-center justify-between py-2 hover:bg-gray-50 px-2 rounded transition-colors duration-200"
                      >
                        <span className="font-semibold text-gray-900">
                          Clase ({filtrosActivos.clase.length > 0 ? filtrosActivos.clase.length : 0})
                        </span>
                        <ChevronDown
                          className={`w-5 h-5 text-gray-500 transition-transform duration-300 ease-in-out ${filtrosAbiertos.clase ? 'rotate-180' : 'rotate-0'
                            }`}
                        />
                      </button>

                      <div
                        style={{
                          maxHeight: filtrosAbiertos.clase ? '200px' : '0',
                          opacity: filtrosAbiertos.clase ? 1 : 0,
                          marginTop: filtrosAbiertos.clase ? '0.5rem' : '0',
                          transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease-in-out, margin-top 0.3s ease-in-out'
                        }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {filtrosDisponibles.clases.map(clase => (
                            <label key={clase} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors duration-150">
                              <input
                                type="checkbox"
                                checked={filtrosActivos.clase.includes(clase)}
                                onChange={() => toggleFiltro('clase', clase)}
                                className="w-4 h-4 text-green-600 rounded focus:ring-green-500 transition-all duration-150"
                              />
                              <span className="text-sm text-gray-700">{clase}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Filtro: Estado */}
                  {filtrosDisponibles.estados && (
                    <div className="border-b border-gray-200 pb-3">
                      <button
                        onClick={() => toggleAcordeonFiltro('estado')}
                        className="w-full flex items-center justify-between py-2 hover:bg-gray-50 px-2 rounded transition-colors duration-200"
                      >
                        <span className="font-semibold text-gray-900">
                          Estado ({filtrosActivos.estado.length > 0 ? filtrosActivos.estado.length : 0})
                        </span>
                        <ChevronDown
                          className={`w-5 h-5 text-gray-500 transition-transform duration-300 ease-in-out ${filtrosAbiertos.estado ? 'rotate-180' : 'rotate-0'
                            }`}
                        />
                      </button>

                      <div
                        style={{
                          maxHeight: filtrosAbiertos.estado ? '200px' : '0',
                          opacity: filtrosAbiertos.estado ? 1 : 0,
                          marginTop: filtrosAbiertos.estado ? '0.5rem' : '0',
                          transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease-in-out, margin-top 0.3s ease-in-out'
                        }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {filtrosDisponibles.estados.map(estado => (
                            <label key={estado} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors duration-150">
                              <input
                                type="checkbox"
                                checked={filtrosActivos.estado.includes(estado)}
                                onChange={() => toggleFiltro('estado', estado)}
                                className="w-4 h-4 text-green-600 rounded focus:ring-green-500 transition-all duration-150"
                              />
                              <span className="text-sm text-gray-700">{estado}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Filtro: Sellos */}
                  {filtrosDisponibles.sellos && filtrosDisponibles.sellos.length > 0 && (
                    <div className="border-b border-gray-200 pb-3">
                      <button
                        onClick={() => toggleAcordeonFiltro('sellos')}
                        className="w-full flex items-center justify-between py-2 hover:bg-gray-50 px-2 rounded transition-colors duration-200"
                      >
                        <span className="font-semibold text-gray-900">
                          Sellos ({filtrosActivos.sellos.length > 0 ? filtrosActivos.sellos.length : 0})
                        </span>
                        <ChevronDown
                          className={`w-5 h-5 text-gray-500 transition-transform duration-300 ease-in-out ${filtrosAbiertos.sellos ? 'rotate-180' : 'rotate-0'
                            }`}
                        />
                      </button>

                      <div
                        style={{
                          maxHeight: filtrosAbiertos.sellos ? '200px' : '0',
                          opacity: filtrosAbiertos.sellos ? 1 : 0,
                          marginTop: filtrosAbiertos.sellos ? '0.5rem' : '0',
                          transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease-in-out, margin-top 0.3s ease-in-out'
                        }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {filtrosDisponibles.sellos.map(sello => (
                            <label key={sello} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors duration-150">
                              <input
                                type="checkbox"
                                checked={filtrosActivos.sellos.includes(sello)}
                                onChange={() => toggleFiltro('sellos', sello)}
                                className="w-4 h-4 text-green-600 rounded focus:ring-green-500 transition-all duration-150"
                              />
                              <span className="text-sm text-gray-700">{sello}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Filtro: Financiación */}
                  {filtrosDisponibles.financiacion && filtrosDisponibles.financiacion.length > 0 && (
                    <div className="border-b border-gray-200 pb-3">
                      <button
                        onClick={() => toggleAcordeonFiltro('financiacion')}
                        className="w-full flex items-center justify-between py-2 hover:bg-gray-50 px-2 rounded transition-colors duration-200"
                      >
                        <span className="font-semibold text-gray-900">
                          Financiación ({filtrosActivos.financiacion.length > 0 ? filtrosActivos.financiacion.length : 0})
                        </span>
                        <ChevronDown
                          className={`w-5 h-5 text-gray-500 transition-transform duration-300 ease-in-out ${filtrosAbiertos.financiacion ? 'rotate-180' : 'rotate-0'
                            }`}
                        />
                      </button>

                      <div
                        style={{
                          maxHeight: filtrosAbiertos.financiacion ? '200px' : '0',
                          opacity: filtrosAbiertos.financiacion ? 1 : 0,
                          marginTop: filtrosAbiertos.financiacion ? '0.5rem' : '0',
                          transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease-in-out, margin-top 0.3s ease-in-out'
                        }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {filtrosDisponibles.financiacion.map(fin => (
                            <label key={fin} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors duration-150">
                              <input
                                type="checkbox"
                                checked={filtrosActivos.financiacion.includes(fin)}
                                onChange={() => toggleFiltro('financiacion', fin)}
                                className="w-4 h-4 text-green-600 rounded focus:ring-green-500 transition-all duration-150"
                              />
                              <span className="text-sm text-gray-700">{fin}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Filtro: Preoferta */}
                  <div className="border-b border-gray-200 pb-3">
                    <button
                      onClick={() => toggleAcordeonFiltro('preoferta')}
                      className="w-full flex items-center justify-between py-2 hover:bg-gray-50 px-2 rounded transition-colors duration-200"
                    >
                      <span className="font-semibold text-gray-900">
                        Preoferta ({filtrosActivos.preoferta.length > 0 ? filtrosActivos.preoferta.length : 0})
                      </span>
                      <ChevronDown
                        className={`w-5 h-5 text-gray-500 transition-transform duration-300 ease-in-out ${filtrosAbiertos.preoferta ? 'rotate-180' : 'rotate-0'
                          }`}
                      />
                    </button>

                    <div
                      style={{
                        maxHeight: filtrosAbiertos.preoferta ? '200px' : '0',
                        opacity: filtrosAbiertos.preoferta ? 1 : 0,
                        marginTop: filtrosAbiertos.preoferta ? '0.5rem' : '0',
                        transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease-in-out, margin-top 0.3s ease-in-out'
                      }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-2">
                        {['Con Preoferta', 'Sin Preoferta'].map(pre => (
                          <label key={pre} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors duration-150">
                            <input
                              type="checkbox"
                              checked={filtrosActivos.preoferta.includes(pre)}
                              onChange={() => toggleFiltro('preoferta', pre)}
                              className="w-4 h-4 text-green-600 rounded focus:ring-green-500 transition-all duration-150"
                            />
                            <span className="text-sm text-gray-700">{pre}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Filtro: Venta */}
                  <div className="border-b border-gray-200 pb-3">
                    <button
                      onClick={() => toggleAcordeonFiltro('venta')}
                      className="w-full flex items-center justify-between py-2 hover:bg-gray-50 px-2 rounded transition-colors duration-200"
                    >
                      <span className="font-semibold text-gray-900">
                        Venta ({filtrosActivos.venta.length > 0 ? filtrosActivos.venta.length : 0})
                      </span>
                      <ChevronDown
                        className={`w-5 h-5 text-gray-500 transition-transform duration-300 ease-in-out ${filtrosAbiertos.venta ? 'rotate-180' : 'rotate-0'
                          }`}
                      />
                    </button>

                    <div
                      style={{
                        maxHeight: filtrosAbiertos.venta ? '200px' : '0',
                        opacity: filtrosAbiertos.venta ? 1 : 0,
                        marginTop: filtrosAbiertos.venta ? '0.5rem' : '0',
                        transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease-in-out, margin-top 0.3s ease-in-out'
                      }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-2">
                        {['Vendido', 'No Vendido'].map(ven => (
                          <label key={ven} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors duration-150">
                            <input
                              type="checkbox"
                              checked={filtrosActivos.venta.includes(ven)}
                              onChange={() => toggleFiltro('venta', ven)}
                              className="w-4 h-4 text-green-600 rounded focus:ring-green-500 transition-all duration-150"
                            />
                            <span className="text-sm text-gray-700">{ven}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Filtro: Peso (inputs) */}
                  <div className="pt-2">
                    <h4 className="font-semibold text-gray-900 mb-3">Peso</h4>
                    <div className="space-y-2">
                      <input
                        type="number"
                        placeholder="Mínimo"
                        value={filtrosActivos.pesoMin}
                        onChange={(e) => setFiltrosActivos(prev => ({ ...prev, pesoMin: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      />
                      <input
                        type="number"
                        placeholder="Máximo"
                        value={filtrosActivos.pesoMax}
                        onChange={(e) => setFiltrosActivos(prev => ({ ...prev, pesoMax: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Botón Limpiar Filtros */}
                {totalFiltrosActivos > 0 && (
                  <button
                    onClick={limpiarFiltros}
                    className="w-full mt-6 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-bold py-4 rounded-xl transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105 flex items-center justify-center gap-2"
                  >
                    <X className="w-5 h-5" />
                    LIMPIAR TODOS LOS FILTROS
                  </button>
                )}
              </div>
            </div>

            {/* ÁREA DE LOTES */}
            <div className="lg:col-span-3">

              {/* Tabs: Vacunos / Ovinos */}
              <div className="flex gap-3 mb-6 bg-white rounded-xl p-2 shadow-lg">
                {tiposDisponibles.map(tipo => (
                  <button
                    key={tipo}
                    onClick={() => setTipoGanado(tipo)}
                    className={`flex-1 px-8 py-4 rounded-lg font-bold transition-all duration-300 ${tipoGanado === tipo
                      ? 'bg-gradient-to-r from-green-600 to-green-500 text-white shadow-xl transform scale-105'
                      : 'bg-transparent text-gray-600 hover:bg-gray-50'
                      }`}
                  >
                    {tipo}
                  </button>
                ))}
              </div>

              {(loading || filtering) ? (
                <div className="text-center py-12">
                  <div className="inline-block w-12 h-12 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                  <p className="mt-4 text-gray-600">Cargando lotes...</p>
                </div>
              ) : lotes.length === 0 ? (
                <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                  <p className="text-gray-600 text-lg">No se encontraron lotes con los filtros seleccionados</p>
                  <button
                    onClick={limpiarFiltros}
                    className="mt-4 text-green-600 hover:text-green-700 font-medium"
                  >
                    Limpiar filtros
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Acordeón por categoría */}
                  {Object.entries(lotesPorCategoria).map(([categoria, lotesCategoria]) => (
                    <LotesCategoriaAccordion
                      key={categoria}
                      categoria={categoria}
                      lotes={lotesCategoria}
                      isOpen={categoriasAbiertas[categoria]}
                      isExpanding={categoriasExpandiendo[categoria]}
                      lotesAbiertos={lotesAbiertos}
                      onToggleCategoria={() => toggleCategoria(categoria)}
                      onToggleLote={toggleLote}
                      onNavigate={navigate}
                      onShare={handleShare}
                      infoRemate={infoRemate}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ModernFooter />

      {/* Modal de Mapa */}
      <MapModal
        isOpen={showMapModal}
        onClose={() => setShowMapModal(false)}
        lotes={lotes}
      />
    </div>
  );
};

export default LotesPage;

